import { useState, useCallback } from 'react';

interface UseImageCropperOptions {
  onCropComplete?: (croppedFile: File, croppedImageUrl: string) => void;
}

interface UseImageCropperReturn {
  cropperOpen: boolean;
  cropperImageSrc: string | null;
  handleImageChange: (file: File | null) => void;
  handleCropComplete: (
    croppedFile: File,
    croppedImageUrl: string,
  ) => void;
  setCropperOpen: (open: boolean) => void;
}

/**
 * Custom hook for managing image cropping functionality
 * Handles file reading, cropper dialog state, and crop completion
 *
 * @param options - Optional callbacks for crop completion
 * @returns Cropper state and handlers
 */
export function useImageCropper(
  options?: UseImageCropperOptions,
): UseImageCropperReturn {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);

  const handleImageChange = useCallback(
    (file: File | null) => {
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageSrc = e.target?.result as string;
        setCropperImageSrc(imageSrc);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    },
    [],
  );

  const handleCropComplete = useCallback(
    (croppedFile: File, croppedImageUrl: string) => {
      setCropperOpen(false);
      setCropperImageSrc(null);
      options?.onCropComplete?.(croppedFile, croppedImageUrl);
    },
    [options],
  );

  return {
    cropperOpen,
    cropperImageSrc,
    handleImageChange,
    handleCropComplete,
    setCropperOpen,
  };
}

